import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Check, Copy, Hash } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export interface MarkdownProps {
  children: string
  className?: string
  /**
   * When set, headings carry stable id attributes (slugified from
   * the heading text) AND a hover-revealed `#` button that copies
   * an anchor URL to the clipboard. Default: false (preserves CARLA
   * chat behaviour where heading anchors aren't useful).
   */
  enableAnchors?: boolean
  /**
   * When set, the parent should also pass the URL prefix used in
   * the copied anchor link (e.g. "/help/modules/contacts"). The hash
   * is appended automatically. Defaults to the current `window.location`
   * pathname when omitted.
   */
  anchorBaseUrl?: string
  /**
   * When set, fires after every heading is mounted so a parent (e.g.
   * a sticky table of contents) can reference DOM anchors. Receives
   * the heading element + its id + level.
   */
  onHeadingMount?: (entry: { id: string; text: string; level: number; el: HTMLHeadingElement }) => void
  /**
   * When the children are HTML (not markdown source) — e.g. when the
   * server has already rendered the markdown via Python markdown lib.
   * Skips the react-markdown parse and renders rehype-raw output.
   *
   * In this mode `enableAnchors` is implied — the server-rendered
   * HTML already carries id attributes, and we wrap the content with
   * a click handler for hover-copy on heading hash buttons.
   */
  asHtml?: boolean
}

/**
 * Thin wrapper around react-markdown with remark-gfm (tables, task lists,
 * autolinks). Component overrides route every element to ops design-token
 * classes so the output lives inside the token system — no raw hex or px.
 *
 * Two modes:
 *  1. asHtml=false (default): pass markdown source as `children`. Used by
 *     CARLA chat for assistant replies (no anchors needed).
 *  2. asHtml=true: pass server-rendered HTML as `children`. Used by the
 *     help portal — server renders via Python's markdown lib so heading
 *     anchor IDs come pre-baked + cmdk index can rely on stable slugs.
 */
export function Markdown({
  children,
  className,
  enableAnchors = false,
  anchorBaseUrl,
  onHeadingMount,
  asHtml = false,
}: MarkdownProps) {
  if (asHtml) {
    return (
      <HtmlContent
        html={children}
        className={className}
        anchorBaseUrl={anchorBaseUrl}
        onHeadingMount={onHeadingMount}
      />
    )
  }
  return (
    <div className={cn('carla-markdown flex flex-col gap-2 text-sm text-primary', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node: _n, ...rest }) => <p className="m-0 leading-snug" {...rest} />,
          a: ({ node: _n, ...rest }) => (
            <a
              className="text-link underline hover:text-link-hover"
              target="_blank"
              rel="noopener noreferrer"
              {...rest}
            />
          ),
          code: ({ node: _n, className: cls, children: kids, ...rest }) => {
            const isBlock = /\blanguage-/.test(cls ?? '')
            if (isBlock) {
              return (
                <code
                  className={cn(
                    'block overflow-x-auto rounded-xs bg-surface-sunk p-2 text-xs text-primary',
                    cls,
                  )}
                  {...rest}
                >
                  {kids}
                </code>
              )
            }
            return (
              <code
                className="rounded-xs bg-surface-sunk px-1 py-0.5 text-xs text-primary"
                {...rest}
              >
                {kids}
              </code>
            )
          },
          pre: ({ node: _n, children, ...rest }) => (
            <CodeBlock {...rest}>{children}</CodeBlock>
          ),
          ul: ({ node: _n, ...rest }) => <ul className="m-0 list-disc pl-5" {...rest} />,
          ol: ({ node: _n, ...rest }) => <ol className="m-0 list-decimal pl-5" {...rest} />,
          li: ({ node: _n, ...rest }) => <li className="leading-snug" {...rest} />,
          table: ({ node: _n, ...rest }) => (
            <div className="overflow-x-auto">
              <table className="border-collapse border border-border-subtle text-xs" {...rest} />
            </div>
          ),
          th: ({ node: _n, ...rest }) => (
            <th
              className="border border-border-subtle bg-surface-sunk px-2 py-1 text-left font-semibold text-primary"
              {...rest}
            />
          ),
          td: ({ node: _n, ...rest }) => (
            <td className="border border-border-subtle px-2 py-1 text-primary" {...rest} />
          ),
          blockquote: ({ node: _n, ...rest }) => (
            <blockquote
              className="m-0 border-l-2 border-border-default pl-3 text-secondary"
              {...rest}
            />
          ),
          h1: ({ node: _n, children: kids, ...rest }) => (
            <Heading level={1} enableAnchor={enableAnchors} baseUrl={anchorBaseUrl} {...rest}>
              {kids}
            </Heading>
          ),
          h2: ({ node: _n, children: kids, ...rest }) => (
            <Heading level={2} enableAnchor={enableAnchors} baseUrl={anchorBaseUrl} {...rest}>
              {kids}
            </Heading>
          ),
          h3: ({ node: _n, children: kids, ...rest }) => (
            <Heading level={3} enableAnchor={enableAnchors} baseUrl={anchorBaseUrl} {...rest}>
              {kids}
            </Heading>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}

const _HEADING_LEVEL_CLASSES: Record<number, string> = {
  1: 'text-lg font-semibold text-primary',
  2: 'text-md font-semibold text-primary',
  3: 'text-sm font-semibold text-primary',
}

/** Heading wrapper used in the markdown-source mode. */
function Heading({
  level,
  enableAnchor,
  baseUrl,
  children,
  ...rest
}: {
  level: 1 | 2 | 3
  enableAnchor: boolean
  baseUrl?: string
  children?: React.ReactNode
}) {
  const text = extractText(children)
  const id = enableAnchor ? slugify(text) : undefined
  const Tag = `h${level}` as 'h1' | 'h2' | 'h3'
  const klass = cn('group m-0 flex items-center gap-2', _HEADING_LEVEL_CLASSES[level])
  return (
    <Tag id={id} className={klass} {...rest}>
      <span>{children}</span>
      {enableAnchor && id ? (
        <AnchorCopyButton anchorId={id} baseUrl={baseUrl} />
      ) : null}
    </Tag>
  )
}

/** Hover-revealed `#` next to a heading; click copies a deep link. */
function AnchorCopyButton({ anchorId, baseUrl }: { anchorId: string; baseUrl?: string }) {
  const [copied, setCopied] = useState(false)

  async function handleClick() {
    const base = baseUrl ?? (typeof window !== 'undefined' ? window.location.pathname : '')
    const url = `${base}#${anchorId}`
    try {
      const fullUrl = typeof window !== 'undefined'
        ? `${window.location.origin}${url}`
        : url
      await navigator.clipboard.writeText(fullUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard API may fail; ignore silently.
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={copied ? 'Anchor copied' : 'Copy anchor link'}
      className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-xs text-tertiary opacity-0 transition-opacity hover:text-primary group-hover:opacity-100 focus-visible:opacity-100"
    >
      {copied ? <Check size={12} /> : <Hash size={12} />}
    </button>
  )
}

/**
 * Server-rendered HTML mode. Used by the help portal: the backend
 * returns HTML with id attributes pre-baked on every heading. We just
 * inject it via dangerouslySetInnerHTML and wire a click handler to
 * heading-anchor copies + a mount-time scan for parent components.
 */
function HtmlContent({
  html,
  className,
  anchorBaseUrl,
  onHeadingMount,
}: {
  html: string
  className?: string
  anchorBaseUrl?: string
  onHeadingMount?: (entry: { id: string; text: string; level: number; el: HTMLHeadingElement }) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const root = containerRef.current
    if (!root) return
    const headings = Array.from(root.querySelectorAll<HTMLHeadingElement>('h1[id], h2[id], h3[id], h4[id]'))
    headings.forEach((h) => {
      const level = Number(h.tagName.slice(1))
      const id = h.id
      const text = h.textContent?.trim() ?? ''
      if (onHeadingMount) onHeadingMount({ id, text, level, el: h })
      // Inject the hover-copy button as a real anchor child.
      if (!h.querySelector('.anchor-copy-btn')) {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.setAttribute('aria-label', 'Copy anchor link')
        btn.className = 'anchor-copy-btn ml-1.5 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-xs text-tertiary opacity-0 align-middle transition-opacity hover:text-primary'
        btn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>'
        btn.onclick = async () => {
          const base = anchorBaseUrl ?? window.location.pathname
          const fullUrl = `${window.location.origin}${base}#${id}`
          try {
            await navigator.clipboard.writeText(fullUrl)
            btn.setAttribute('aria-label', 'Anchor copied')
            setTimeout(() => btn.setAttribute('aria-label', 'Copy anchor link'), 1500)
          } catch {
            /* ignore */
          }
        }
        // Show the button on hover of the heading.
        h.addEventListener('mouseenter', () => { btn.style.opacity = '1' })
        h.addEventListener('mouseleave', () => { btn.style.opacity = '0' })
        h.classList.add('group', 'flex', 'items-center', 'gap-1')
        h.appendChild(btn)
      }
    })
  }, [html, anchorBaseUrl, onHeadingMount])

  return (
    <div
      ref={containerRef}
      className={cn(
        'help-html flex flex-col gap-3 text-sm text-primary',
        '[&_h1]:m-0 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:text-primary [&_h1]:mt-2',
        '[&_h2]:m-0 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-primary [&_h2]:mt-4',
        '[&_h3]:m-0 [&_h3]:text-md [&_h3]:font-semibold [&_h3]:text-primary [&_h3]:mt-3',
        '[&_p]:m-0 [&_p]:leading-snug',
        '[&_a]:text-link [&_a]:underline hover:[&_a]:text-link-hover',
        '[&_ul]:m-0 [&_ul]:list-disc [&_ul]:pl-5',
        '[&_ol]:m-0 [&_ol]:list-decimal [&_ol]:pl-5',
        '[&_li]:leading-snug',
        '[&_blockquote]:m-0 [&_blockquote]:border-l-2 [&_blockquote]:border-border-default [&_blockquote]:pl-3 [&_blockquote]:text-secondary',
        '[&_table]:border-collapse [&_table]:border [&_table]:border-border-subtle [&_table]:text-xs',
        '[&_th]:border [&_th]:border-border-subtle [&_th]:bg-surface-sunk [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold',
        '[&_td]:border [&_td]:border-border-subtle [&_td]:px-2 [&_td]:py-1',
        '[&_code]:rounded-xs [&_code]:bg-surface-sunk [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs',
        '[&_pre]:m-0 [&_pre]:overflow-x-auto [&_pre]:rounded-sm [&_pre]:bg-surface-sunk [&_pre]:p-3',
        '[&_hr]:my-4 [&_hr]:border-border-subtle',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

/**
 * <pre><code> block with a hover-revealed copy button. Reads the first
 * child <code>'s text content to support single-line copy.
 */
function CodeBlock({ children, ...rest }: React.HTMLAttributes<HTMLPreElement>) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    const text = extractText(children)
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard API can fail if the surface isn't focused; fall back silently.
    }
  }

  return (
    <div className="group relative">
      <pre
        className="m-0 overflow-x-auto rounded-sm bg-surface-sunk p-3 pr-10"
        {...rest}
      >
        {children}
      </pre>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy code'}
        className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs bg-surface text-tertiary opacity-0 shadow-sm transition-opacity hover:text-primary group-hover:opacity-100 focus-visible:opacity-100"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </div>
  )
}

function extractText(node: React.ReactNode): string {
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractText).join('')
  if (node && typeof node === 'object' && 'props' in node) {
    return extractText((node as { props: { children?: React.ReactNode } }).props.children)
  }
  return ''
}

const _SLUG_STRIP = /[^a-z0-9\s-]/g
const _SLUG_SPACES = /\s+/g

/**
 * Mirror of the server-side slugify in app/api/routes/help.py so the
 * markdown-source mode produces the same anchor ids the server-rendered
 * mode produces. Deep-link URLs survive the round-trip.
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(_SLUG_STRIP, '')
    .replace(_SLUG_SPACES, '-')
    .replace(/^-+|-+$/g, '')
}
